import { API_BASE, authHeaders } from './apiAuth'

/**
 * The Administrator skill, as the website and the Miro panel see it
 * (workers/api/src/routes/admin.ts).
 */

async function reason(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as { error?: string } | null
  return body?.error ?? `HTTP ${response.status}`
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(await authHeaders()),
      ...(init.headers ?? {}),
    },
  })
  if (!response.ok) throw new Error(await reason(response))
  return (await response.json()) as T
}

const post = <T>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) })

/* ---------- everyone ---------- */

export interface Me {
  email: string
  admin: boolean
}

/** Who this is, and whether an administrator: the server's answer, not a list in the page. */
export const readMe = () => call<Me>('/api/me')

export interface ShownAnnouncement {
  id: string
  body: string
  /** A data: URL, or ''. */
  image: string
  /** Paths it shows on; empty means every page. */
  pages: string[]
}

export const readMyAnnouncements = () => call<ShownAnnouncement[]>('/api/announcements')
export const dismissAnnouncement = (id: string) => post<{ ok: true }>('/api/announcements/dismiss', { id })

/** Does an announcement belong on this page? '/' only on the overview itself. */
export function announcementOnPage(pages: string[], path: string): boolean {
  if (pages.length === 0) return true
  return pages.some((page) => (page === '/' ? path === '/' : path === page || path.startsWith(`${page}/`)))
}

/* ---------- administrators ---------- */

export interface Announcement {
  id: string
  body: string
  image: string
  startsAt: string
  endsAt: string | null
  pages: string[]
  audience: string[]
  createdBy: string
  createdAt: string
  trashedAt: string | null
}

export interface NewAnnouncement {
  body: string
  image: string
  startsAt: string
  endsAt: string | null
  pages: string[]
  audience: string[]
}

export const listAnnouncements = () => call<Announcement[]>('/api/admin/announcements')
export const createAnnouncement = (ask: NewAnnouncement) => post<Announcement>('/api/admin/announcements', ask)
export const trashAnnouncement = (id: string) => post<{ ok: true }>('/api/admin/announcements/trash', { id })
export const restoreAnnouncement = (id: string) => post<{ ok: true }>('/api/admin/announcements/restore', { id })
export const deleteAnnouncement = (id: string) => post<{ ok: true }>('/api/admin/announcements/delete', { id })

export interface AdminsView {
  admins: { email: string; name: string; since: string }[]
  /** Everyone who has signed in, to choose from. */
  people: { email: string; name: string }[]
}

export const readAdmins = () => call<AdminsView>('/api/admin/admins')
export const addAdmin = (email: string) => post<{ ok: true }>('/api/admin/admins', { email })
export const removeAdmin = (email: string) => post<{ ok: true; self: boolean }>('/api/admin/admins/remove', { email })

/**
 * A picture for an announcement, made small enough to live on its row:
 * at most 1600 px across, JPEG. Read here in the browser; nothing is uploaded
 * until the announcement is sent.
 */
export async function announcementPicture(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const pen = canvas.getContext('2d')
  if (!pen) throw new Error('This browser cannot read that picture')
  pen.fillStyle = '#ffffff'
  pen.fillRect(0, 0, canvas.width, canvas.height)
  pen.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  for (const quality of [0.85, 0.75, 0.6]) {
    const url = canvas.toDataURL('image/jpeg', quality)
    if (url.length < 1_500_000) return url
  }
  throw new Error('That picture is too large even when made smaller')
}

/* ---------- the web pages' storage (docs/web-storage.sql, 2026-10-21) ---------- */

export type WebPage = 'generate' | 'upscale' | 'video'

export interface WebStoragePage {
  slug: 'image-generation' | 'upscale' | 'video-generation'
  runs: number
  files: number
  bytes: number
  people: number
  before_runs: number
  before_files: number
  before_bytes: number
  before_people: number
  oldest: string | null
}

export interface WebStorageReport {
  before: string
  projects: { files: number; bytes: number; projects: number }
  hiddenOther: { files: number; bytes: number }
  pages: WebStoragePage[]
}

export interface WebStorageCleared {
  runs: number
  files: number
  bytes: number
  runsLeft: number
}

export const readWebStorage = (before: string) =>
  call<WebStorageReport>(`/api/admin/storage?before=${encodeURIComponent(before)}`)

/** One batch; call again while runsLeft is above nought. */
export const clearWebStorage = (page: WebPage, before: string) =>
  post<WebStorageCleared>('/api/admin/storage/clear', { page, before, confirm: 'DELETE' })
